import React, { useState, useEffect, useRef, useContext } from 'react';
import { Box, Typography, Paper, IconButton, Collapse, Dialog, DialogTitle, 
         DialogContent, DialogActions, Button, Tooltip } from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ClearIcon from '@mui/icons-material/Clear';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CodeIcon from '@mui/icons-material/Code';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { LogContext } from '../context/LogContext';

const LogComponent = ({ logs, maxLogs = 100, maxHeight = 300 }) => {
  const [expanded, setExpanded] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedLogData, setSelectedLogData] = useState(null);
  const [dialogTitle, setDialogTitle] = useState('');
  const [copiedStatus, setCopiedStatus] = useState(false);
  const logEndRef = useRef(null);
  const { clearLogs } = useContext(LogContext);
  
  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (expanded && logEndRef.current) {
      logEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, expanded]);
  
  // Get only the most recent logs up to maxLogs
  const recentLogs = logs.slice(-maxLogs);
  
  // Function to determine log color based on type
  const getLogColor = (type) => {
    switch (type) {
      case 'error':
        return '#f44336';
      case 'warning':
        return '#ff9800';
      case 'success':
        return '#4caf50';
      case 'info':
        return '#2196f3';
      case 'ai':
        return '#9c27b0';
      case 'layout':
        return '#00bcd4';
      case 'photos':
        return '#673ab7';
      default:
        return 'inherit';
    }
  };

  // Format timestamp
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  };
  
  // Open dialog with JSON data
  const handleOpenJsonDialog = (data, title) => {
    setSelectedLogData(data);
    setDialogTitle(title || 'Log Details');
    setDialogOpen(true);
  };

  // Close dialog
  const handleCloseDialog = () => {
    setDialogOpen(false);
  };
  
  // Truncate JSON for display
  const truncateJson = (jsonData, maxLength = 150) => {
    const str = JSON.stringify(jsonData);
    return str.length > maxLength ? str.substring(0, maxLength) + '...' : str;
  };
  
  // Returns true if the log entry has AI request/response data
  const hasAiData = (log) => {
    return log.type === 'ai' && 
           log.data && 
           (log.data.prompt || 
            log.data.response || 
            log.data.fullLayoutPlan || 
            (typeof log.data === 'object' && log.data.debugMode !== undefined) ||
            log.message.includes('Calling OpenAI API') || 
            log.message.includes('Received response') ||
            log.message.includes('Processing layout plan') ||
            log.message.includes('Adjusting font size'));
  };

  return (
    <Paper 
      elevation={3}
      sx={{
        position: 'fixed',
        bottom: 0,
        right: 20,
        width: 400,
        maxWidth: '90vw',
        zIndex: 1000,
        borderTopLeftRadius: 8,
        borderTopRightRadius: 8,
        overflow: 'hidden',
      }}
    >
      <Box 
        sx={{
          p: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: '#333',
          color: 'white',
          cursor: 'pointer',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Typography variant="subtitle2">
          Application Logs {logs.length > 0 && `(${logs.length})`}
        </Typography>
        <Box>
          <IconButton 
            size="small" 
            color="inherit"
            onClick={(e) => {
              e.stopPropagation();
              if (clearLogs) {
                clearLogs();
              }
            }}
          >
            <ClearIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="inherit">
            {expanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
          </IconButton>
        </Box>
      </Box>
      
      <Collapse in={expanded}>
        <Box 
          sx={{
            p: 1,
            maxHeight,
            overflowY: 'auto',
            bgcolor: '#1e1e1e',
            color: '#f5f5f5',
            fontFamily: 'monospace',
          }}
        >
          {recentLogs.length === 0 ? (
            <Typography variant="body2" sx={{ color: '#888', fontStyle: 'italic', p: 1 }}>
              No logs to display
            </Typography>
          ) : (
            recentLogs.map((log, index) => (
              <Box key={index} sx={{ mb: 1, p: 0.5, borderLeft: `3px solid ${getLogColor(log.type)}` }}>
                <Typography variant="caption" sx={{ color: '#888', mr: 1 }}>
                  [{formatTime(log.timestamp)}]
                </Typography>
                <Typography
                  variant="body2"
                  component="span"
                  sx={{
                    color: getLogColor(log.type),
                    fontWeight: 'bold',
                    mr: 1,
                  }}
                >
                  {log.type.toUpperCase()}:
                </Typography>
                <Typography
                  variant="body2"
                  component="span"
                  sx={{
                    wordBreak: 'break-word',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {log.message}
                </Typography>
                {log.data && (
                  <Box sx={{ mt: 0.5, pl: 2 }}>
                    {hasAiData(log) ? (
                      <Box>
                        {(log.message.includes('Calling OpenAI API') || log.data?.type === 'AI_REQUEST') && (
                          <Box sx={{ 
                            backgroundColor: 'rgba(76, 175, 80, 0.1)', 
                            borderLeft: '3px solid #4caf50',
                            p: 0.5,
                            mb: 0.5
                          }}>
                            <Typography variant="body2" sx={{ color: '#81c784', fontSize: '0.85em', fontWeight: 'bold' }}>
                              AI Request Details:
                            </Typography>
                            {log.data?.debugMode !== undefined && (
                              <Typography variant="body2" sx={{ color: '#b39ddb', fontSize: '0.85em' }}>
                                Debug mode: {log.data.debugMode ? 'ON' : 'OFF'}
                              </Typography>
                            )}
                            {log.data?.model && (
                              <Typography variant="body2" sx={{ color: '#81c784', fontSize: '0.85em' }}>
                                Model: {log.data.model}
                              </Typography>
                            )}
                            {log.data?.requestTime && (
                              <Typography variant="body2" sx={{ color: '#81c784', fontSize: '0.85em' }}>
                                Time: {new Date(log.data.requestTime).toLocaleTimeString()}
                              </Typography>
                            )}
                            {(log.data?.prompt || log.data?.promptLength) && (
                              <Typography variant="body2" sx={{ color: '#81c784', fontSize: '0.75em' }}>
                                Text length: {log.data.promptLength || (log.data.prompt && log.data.prompt.length) || 'unknown'} chars
                              </Typography>
                            )}
                          </Box>
                        )}
                        
                        {(log.data?.prompt || log.data?.fullPrompt || log.data?.promptPreview) && (
                          <Box sx={{ my: 1 }}>
                            <Typography variant="body2" sx={{ color: '#81c784', fontSize: '0.85em', fontWeight: 'bold' }}>
                              AI Request:
                            </Typography>
                            <Box 
                              sx={{ 
                                backgroundColor: 'rgba(0,0,0,0.2)', 
                                p: 1, 
                                borderRadius: 1,
                                maxHeight: '100px',
                                overflowY: 'auto',
                                cursor: 'pointer',
                                '&:hover': { backgroundColor: 'rgba(0,0,0,0.3)' },
                              }}
                              onClick={() => handleOpenJsonDialog(log.data.fullPrompt || log.data.prompt, 'AI Request Text')}
                            >
                              <Typography variant="body2" sx={{ color: '#aaa', fontSize: '0.85em' }}>
                                {log.data.promptPreview || 
                                  (typeof log.data.prompt === 'string' 
                                    ? (log.data.prompt.length > 150 
                                      ? log.data.prompt.substring(0, 150) + '...' 
                                      : log.data.prompt)
                                    : truncateJson(log.data.prompt)
                                  )
                                }
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#777', fontSize: '0.7em', display: 'block', textAlign: 'right', mt: 1 }}>
                                Click to view full prompt
                              </Typography>
                            </Box>
                          </Box>
                        )}
                        
                        {(log.data?.response || log.message.includes('Received response') || log.data?.type === 'AI_RESPONSE') && (
                          <Box sx={{ 
                            my: 1,
                            backgroundColor: 'rgba(33, 150, 243, 0.1)', 
                            borderLeft: '3px solid #2196f3',
                            p: 0.5
                          }}>
                            <Typography variant="body2" sx={{ color: '#4fc3f7', fontSize: '0.85em', fontWeight: 'bold' }}>
                              AI Response:
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', mb: 1 }}>
                              {log.data?.responseDataLength && (
                                <Typography variant="body2" sx={{ color: '#90caf9', fontSize: '0.75em', mr: 2 }}>
                                  Length: {log.data.responseDataLength} chars
                                </Typography>
                              )}
                              {log.data?.responseTime && (
                                <Typography variant="body2" sx={{ color: '#90caf9', fontSize: '0.75em', mr: 2 }}>
                                  Time: {new Date(log.data.responseTime).toLocaleTimeString()}
                                </Typography>
                              )}
                              {log.data?.model && (
                                <Typography variant="body2" sx={{ color: '#90caf9', fontSize: '0.75em', mr: 2 }}>
                                  Model: {log.data.model}
                                </Typography>
                              )}
                              {log.data?.totalTokenCount && (
                                <Typography variant="body2" sx={{ color: '#90caf9', fontSize: '0.75em' }}>
                                  Tokens: {log.data.totalTokenCount} ({log.data.promptTokenCount} prompt + {log.data.completionTokenCount} completion)
                                </Typography>
                              )}
                            </Box>
                            <Box 
                              sx={{ 
                                backgroundColor: 'rgba(0,0,0,0.2)', 
                                p: 1, 
                                mt: 0.5,
                                borderRadius: 1,
                                maxHeight: '100px',
                                overflowY: 'auto',
                                cursor: 'pointer',
                                '&:hover': { backgroundColor: 'rgba(0,0,0,0.3)' },
                              }}
                              onClick={() => handleOpenJsonDialog(
                                log.data?.response || log.data?.fullLayoutPlan || log.data, 
                                'AI Response'
                              )}
                            >
                              <Typography variant="body2" sx={{ color: '#aaa', fontSize: '0.85em' }}>
                                {log.data?.response && typeof log.data.response === 'string'
                                  ? (log.data.response.length > 150 
                                    ? log.data.response.substring(0, 150) + '...' 
                                    : log.data.response)
                                  : truncateJson(log.data?.response || log.data?.fullLayoutPlan || log.data)
                                }
                              </Typography>
                            </Box>
                          </Box>
                        )}
                        
                        {log.data?.type === 'AI_ERROR' && (
                          <Box sx={{ 
                            my: 1,
                            backgroundColor: 'rgba(244, 67, 54, 0.1)', 
                            borderLeft: '3px solid #f44336',
                            p: 0.5
                          }}>
                            <Typography variant="body2" sx={{ color: '#ef9a9a', fontSize: '0.85em', fontWeight: 'bold' }}>
                              AI Error: {log.data.errorType || 'Unknown Error'}
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', mb: 1 }}>
                              {log.data?.timestamp && (
                                <Typography variant="body2" sx={{ color: '#ef9a9a', fontSize: '0.75em', mr: 2 }}>
                                  Time: {new Date(log.data.timestamp).toLocaleTimeString()}
                                </Typography>
                              )}
                              {log.data?.status && (
                                <Typography variant="body2" sx={{ color: '#ef9a9a', fontSize: '0.75em', mr: 2 }}>
                                  Status: {log.data.status}
                                </Typography>
                              )}
                              {log.data?.waitMs && (
                                <Typography variant="body2" sx={{ color: '#ef9a9a', fontSize: '0.75em', mr: 2 }}>
                                  Wait: {log.data.waitMs}ms
                                </Typography>
                              )}
                              {log.data?.retryCount !== undefined && (
                                <Typography variant="body2" sx={{ color: '#ef9a9a', fontSize: '0.75em' }}>
                                  Retry: {log.data.retryCount}
                                </Typography>
                              )}
                            </Box>
                            {log.data?.message && (
                              <Box 
                                sx={{ 
                                  backgroundColor: 'rgba(0,0,0,0.2)', 
                                  p: 1, 
                                  mt: 0.5,
                                  borderRadius: 1,
                                  maxHeight: '100px',
                                  overflowY: 'auto',
                                }}
                              >
                                <Typography variant="body2" sx={{ color: '#ffcdd2', fontSize: '0.85em' }}>
                                  {log.data.message}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        )}
                        
                        {log.data?.type === 'AI_RESULT_PROCESSING' && (
                          <Box sx={{ 
                            my: 1,
                            backgroundColor: 'rgba(156, 39, 176, 0.1)', 
                            borderLeft: '3px solid #9c27b0',
                            p: 0.5
                          }}>
                            <Typography variant="body2" sx={{ color: '#ce93d8', fontSize: '0.85em', fontWeight: 'bold' }}>
                              AI Result Processing:
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', mb: 1 }}>
                              {log.data?.layoutCount && (
                                <Typography variant="body2" sx={{ color: '#ce93d8', fontSize: '0.75em', mr: 2 }}>
                                  Layouts: {log.data.layoutCount}
                                </Typography>
                              )}
                              {log.data?.totalPhotosUsed !== undefined && (
                                <Typography variant="body2" sx={{ color: '#ce93d8', fontSize: '0.75em', mr: 2 }}>
                                  Photos used: {log.data.totalPhotosUsed}
                                </Typography>
                              )}
                              {log.data?.timestamp && (
                                <Typography variant="body2" sx={{ color: '#ce93d8', fontSize: '0.75em' }}>
                                  Time: {new Date(log.data.timestamp).toLocaleTimeString()}
                                </Typography>
                              )}
                            </Box>
                            {log.data?.layoutIds && log.data.layoutIds.length > 0 && (
                              <Typography variant="body2" sx={{ color: '#e1bee7', fontSize: '0.75em' }}>
                                Layout IDs: {log.data.layoutIds.join(', ')}
                              </Typography>
                            )}
                          </Box>
                        )}
                        
                        {!log.data.prompt && !log.data.response && (
                          <Box 
                            sx={{ 
                              display: 'flex',
                              alignItems: 'center'
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#999',
                                fontSize: '0.85em',
                                wordBreak: 'break-word',
                                whiteSpace: 'pre-wrap',
                                flexGrow: 1
                              }}
                            >
                              {typeof log.data === 'object' 
                                ? truncateJson(log.data)
                                : log.data
                              }
                            </Typography>
                            <Tooltip title="View Complete Data">
                              <IconButton 
                                size="small" 
                                sx={{ ml: 1, color: '#777' }}
                                onClick={() => handleOpenJsonDialog(log.data, log.message)}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </Box>
                    ) : (
                      <Box 
                        sx={{ 
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#999',
                            fontSize: '0.85em',
                            wordBreak: 'break-word',
                            whiteSpace: 'pre-wrap',
                            flexGrow: 1
                          }}
                        >
                          {typeof log.data === 'object' 
                            ? truncateJson(log.data)
                            : log.data
                          }
                        </Typography>
                        
                        {typeof log.data === 'object' && (
                          <Tooltip title="View Complete Data">
                            <IconButton 
                              size="small" 
                              sx={{ ml: 1, color: '#777' }}
                              onClick={() => handleOpenJsonDialog(log.data, log.message)}
                            >
                              <CodeIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            ))
          )}
          <div ref={logEndRef} />
        </Box>
      </Collapse>
      
      {/* JSON Data Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ 
          backgroundColor: '#333',
          color: 'white',
          display: 'flex',
          alignItems: 'center'
        }}>
          {dialogTitle.includes('AI') ? <CodeIcon sx={{ mr: 1 }} /> : <VisibilityIcon sx={{ mr: 1 }} />}
          {dialogTitle}
        </DialogTitle>
        <DialogContent dividers>
          <Box
            sx={{
              backgroundColor: '#1e1e1e',
              color: '#f5f5f5',
              p: 2,
              borderRadius: 1,
              fontFamily: 'monospace',
              overflowX: 'auto',
              maxHeight: '60vh'
            }}
          >
            {selectedLogData ? (
              typeof selectedLogData === 'string' ? (
                <pre style={{ 
                  margin: 0, 
                  whiteSpace: 'pre-wrap', 
                  wordBreak: 'break-word',
                  fontFamily: 'monospace',
                  color: dialogTitle?.includes('AI') ? '#81c784' : 'inherit',
                  backgroundColor: dialogTitle?.includes('AI') ? 'rgba(0,0,0,0.05)' : 'transparent',
                  padding: '10px',
                  borderRadius: '4px',
                  maxWidth: '100%',
                  overflowX: 'auto'
                }}>
                  {selectedLogData}
                </pre>
              ) : (
                <pre 
                  style={{ 
                    margin: 0, 
                    lineHeight: 1.5,
                    fontSize: '14px',
                    fontFamily: 'monospace',
                    backgroundColor: 'rgba(0,0,0,0.05)',
                    padding: '10px',
                    borderRadius: '4px',
                    maxWidth: '100%',
                    overflowX: 'auto'
                  }}
                >
                  {JSON.stringify(selectedLogData, null, 2)}
                </pre>
              )
            ) : 'No data available'}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              const textToCopy = typeof selectedLogData === 'string' 
                ? selectedLogData 
                : JSON.stringify(selectedLogData, null, 2);
              
              navigator.clipboard.writeText(textToCopy)
                .then(() => {
                  // Set copied state instead of showing alert
                  setCopiedStatus(true);
                  // Reset after 2 seconds
                  setTimeout(() => setCopiedStatus(false), 2000);
                })
                .catch(err => {
                  console.error('Failed to copy to clipboard:', err);
                });
            }} 
            color="secondary"
            startIcon={copiedStatus ? <CheckIcon /> : <ContentCopyIcon />}
          >
            {copiedStatus ? 'Copied!' : 'Copy to Clipboard'}
          </Button>
          <Button onClick={handleCloseDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default LogComponent;
